import React, { useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Typography, Paper, IconButton, Tooltip, styled, Box, Checkbox, Popover } from '@mui/material';
import { Folder, Image, StorageScheme } from '../models';
import * as FolderStore from '../store/Folder';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import { useNavigate } from 'react-router-dom';
import { stopClickPropogation } from '../utils';
import CopyButton from './CopyButton';
import _ from 'lodash';

import FolderIcon from '@mui/icons-material/FolderOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import FolderUpIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import MoveIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import DownloadImageButton from './DownloadImageButton';

function ThumbnailPreview(props: { url: string }) {
    const anchorEl = React.useRef<null | HTMLImageElement>(null);
    const [open, setOpen] = React.useState<boolean>(false);

    const handleHover = (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        setOpen(true);
    };

    const handleBlur = (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        setOpen(false);
    };

    const handleClick = (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
    };

    return (
        <>
            <Popover
                open={open}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <img src={`${props.url}?w=400`} />
            </Popover>
            <img
                ref={anchorEl}
                src={`${props.url}?w=40`}
                style={{ marginRight: 8 }}
                width={40}
                loading="lazy"
            />
        </>
    )
}

interface FilesTableProps {
    folders: Folder[];
    images: Image[];
    currentFolder: Folder;
    selectedImageIds: string[];
    onRefresh: (folder: Folder) => void;
    onCreateFolder: (parentFolder: Folder) => void;
    onDeleteFolder: (folder: Folder) => void;
    onDeleteImages: (images: Image[]) => void;
    onMoveImages: (images: Image[]) => void;
    onMoveFolder: (folder: Folder) => void;
    onToggleAllImages: () => void;
    onCheckImage: (image: Image) => void;
}

const StyledRoot = styled('div')({
    width: '100%'
});

const StyledPaper = styled(Paper)(({ theme }) => ({
    width: '100%',
    marginBottom: theme.spacing(2)
}));

const StyledTable = styled(Table)({
    minWidth: 750
});

const ActionsTableCell = styled(TableCell)({
    width: 150
});

const StorageSchemeBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',

    '& i': {
        marginRight: 4,
        color: theme.palette.grey[700]
    }
}));

export default function FilesTable(props: FilesTableProps) {
    const foldersFlattened = useSelector(FolderStore.selectors.foldersFlattened);
    const parentFolder = foldersFlattened.find(f => f.id === props.currentFolder.parentId);
    const anyImageSelected = props.selectedImageIds.length > 0;
    const imagesSorted = _.orderBy(props.images, [image => image.fileName.toLowerCase()], ['asc']);
    const foldersSorted = _.orderBy(props.folders, [folder => folder.name.toLowerCase()], ['asc']);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const createDeleteImageCallback = (image: Image) => (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        props.onDeleteImages([image]);
    }

    const createMoveImageCallback = (image: Image) => (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        props.onMoveImages([image]);
    }

    const createDeleteFolderCallback = (folder: Folder) => (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        props.onDeleteFolder(folder);
    }

    const createMoveFolderCallback = (folder: Folder) => (event: React.MouseEvent<unknown>) => {
        event.stopPropagation();
        props.onMoveFolder(folder);
    }

    const createFolderClickCallback = (folder: Folder) => () => {
        navigate(`/folder/${folder.id}`);
    }

    const createImageClickCallback = (image: Image) => () => {
        navigate(`/image/${image.id}`);
    }

    const isImageSelected = (image: Image) => {
        return props.selectedImageIds.includes(image.id);
    }

    const allImagesSelected = () => {
        return props.images.length > 0 && props.images.length === props.selectedImageIds.length;
    }

    const createCheckImageCallback = (image: Image) => (event: React.ChangeEvent<HTMLInputElement>) => {
        props.onCheckImage(image);
    }

    const renderParentFolderRow = () => {
        if (!parentFolder) {
            return null;
        }

        return (
            <TableRow hover tabIndex={-1} sx={{ cursor: 'pointer', height: '53px' }} onClick={createFolderClickCallback(parentFolder)} >
                <TableCell padding="checkbox"></TableCell>
                <TableCell align="left"><Box sx={{ display: 'flex', alignItems: 'center' }}><FolderUpIcon sx={{ marginRight: 2 }} /> ...</Box></TableCell>
                <TableCell align="left"></TableCell>
                <TableCell align="left"></TableCell>
                <ActionsTableCell />
            </TableRow>
        );
    }

    const renderFolderRow = (folder: Folder) => {
        return (
            <TableRow key={folder.id} hover tabIndex={-1} sx={{ cursor: 'pointer', height: '53px' }} onClick={createFolderClickCallback(folder)} >
                <TableCell padding="checkbox"></TableCell>
                <TableCell align="left"><Box sx={{ display: 'flex', alignItems: 'center' }}><FolderIcon sx={{ marginRight: 3.25 }} /> {folder.name}</Box></TableCell>
                <TableCell align="left"></TableCell>
                <TableCell align="left"></TableCell>
                <ActionsTableCell align="right" onClick={stopClickPropogation}>
                    <Tooltip title="Move folder">
                        <>
                            <IconButton onClick={createMoveFolderCallback(folder)} disabled={anyImageSelected}>
                                <MoveIcon />
                            </IconButton>
                        </>
                    </Tooltip>
                    <Tooltip title="Delete folder">
                        <>
                            <IconButton onClick={createDeleteFolderCallback(folder)} disabled={anyImageSelected}>
                                <DeleteIcon />
                            </IconButton>
                        </>
                    </Tooltip>
                </ActionsTableCell>
            </TableRow>
        );
    };

    const renderImageRow = (image: Image) => {
        return (
            <TableRow key={image.id} hover tabIndex={-1} sx={{ cursor: 'pointer', height: '53px' }} onClick={createImageClickCallback(image)} >
                <TableCell padding="checkbox" onClick={stopClickPropogation}>
                    <Checkbox color="primary" checked={isImageSelected(image)} onChange={createCheckImageCallback(image)} onClick={stopClickPropogation} />
                </TableCell>
                <TableCell align="left">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ThumbnailPreview url={image.resizeUrl} />
                        {image.fileName}
                    </Box>
                </TableCell>
                <TableCell align="left"><div onClick={stopClickPropogation} style={{ display: 'inline-block' }}><CopyButton text={image.referenceRawUrl || image.rawUrl}>{image.referenceRawUrl || image.rawUrl}</CopyButton></div></TableCell>
                <TableCell align="left">{renderStorageScheme(image)}</TableCell>
                <ActionsTableCell align="right" onClick={stopClickPropogation}>
                    <Tooltip title="Download image">
                        <DownloadImageButton image={image} />
                    </Tooltip>
                    <Tooltip title="Move image">
                        <>
                            <IconButton onClick={createMoveImageCallback(image)} disabled={anyImageSelected}>
                                <MoveIcon />
                            </IconButton>
                        </>
                    </Tooltip>
                    <Tooltip title="Delete image">
                        <>
                            <IconButton onClick={createDeleteImageCallback(image)} disabled={anyImageSelected}>
                                <DeleteIcon />
                            </IconButton>
                        </>
                    </Tooltip>
                </ActionsTableCell>
            </TableRow >
        );
    };

    const renderStorageScheme = (image: Image) => {
        const icon = image.storageScheme === StorageScheme.Embedded ? <MenuBookIcon /> : <PublicIcon />;
        const name = image.storageScheme === StorageScheme.Embedded ? 'Product Catalog Embedded' : 'Independent';
        return <StorageSchemeBox><i>{icon}</i> {name}</StorageSchemeBox>;
    }

    return (
        <StyledRoot>
            <StyledPaper elevation={0}>
                <TableContainer>
                    <StyledTable
                        aria-labelledby="tableTitle"
                        size='small'
                        aria-label="enhanced table"
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    {props.images.length > 0 && <Checkbox
                                        color="primary"
                                        checked={allImagesSelected()}
                                        onChange={props.onToggleAllImages}
                                        onClick={stopClickPropogation} />}
                                </TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Reference Url</TableCell>
                                <TableCell>Storage Scheme</TableCell>
                                {/* Additional blank column for actions */}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {renderParentFolderRow()}
                            {foldersSorted.map(f => renderFolderRow(f))}
                            {imagesSorted.map(i => renderImageRow(i))}
                        </TableBody>
                    </StyledTable>
                </TableContainer>
            </StyledPaper>
        </StyledRoot>
    );
}